import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: Code, 3: Nouveau mot de passe
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async () => {
    setError('');
    setLoading(true);
    
    try {
      const response = await authService.forgotPassword(email);
      
      // En mode développement, afficher le code dans la console
      if (process.env.NODE_ENV !== 'production' && response.devCode) {
        console.log('Code de vérification (DEV):', response.devCode);
        alert(`Code de développement: ${response.devCode}\n(En production, ce code serait envoyé par email)`);
      }
      
      setSuccess(response.message || 'Un code de vérification a été envoyé à votre email');
      setTimeout(() => {
        setSuccess('');
        setStep(2);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeVerification = async () => {
    setError('');
    setLoading(true);
    
    try {
      await authService.verifyResetCode(email, code);
      setSuccess('Code vérifié avec succès !');
      setTimeout(() => {
        setSuccess('');
        setStep(3);
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Code de vérification incorrect');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setError('');
    
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    
    try {
      await authService.resetPassword(email, code, newPassword);
      setSuccess('Mot de passe réinitialisé avec succès !');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
              Mot de passe oublié ?
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Entrez votre adresse email pour recevoir un code de vérification
            </Typography>

            <TextField
              fullWidth
              label="Adresse Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleEmailSubmit}
              disabled={!email || loading}
            >
              {loading ? 'Envoi en cours...' : 'Envoyer le code'}
            </Button>
          </>
        );

      case 2:
        return (
          <>
            <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
              Vérification
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Entrez le code à 6 chiffres envoyé à {email}
            </Typography>

            <TextField
              fullWidth
              label="Code de vérification"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError('');
              }}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleCodeVerification}
              disabled={code.length !== 6 || loading}
            >
              {loading ? 'Vérification...' : 'Vérifier le code'}
            </Button>

            <Button
              fullWidth
              variant="text"
              sx={{ mt: 2 }}
              onClick={() => setStep(1)}
            >
              Renvoyer un code
            </Button>
          </>
        );

      case 3:
        return (
          <>
            <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
              Nouveau mot de passe
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Choisissez un nouveau mot de passe sécurisé
            </Typography>

            <TextField
              fullWidth
              label="Nouveau mot de passe"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError('');
              }}
              helperText="Minimum 8 caractères"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError('');
              }}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handlePasswordReset}
              disabled={!newPassword || !confirmPassword || loading}
              startIcon={<CheckCircle />}
            >
              {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={(theme) => ({
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.background.default,
        p: 2
      })}
    >
      <Card sx={{ maxWidth: 500, width: '100%', boxShadow: 6 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Logo SG DIGITAL */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #1F2937 0%, #4B5563 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}>
              Facteio
            </Typography>
          </Box>

          {/* Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Contenu de l'étape */}
          {renderStepContent()}

          {/* Lien retour connexion */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              sx={{ cursor: 'pointer' }}
            >
              ← Retour à la connexion
            </Link>
          </Box>

          {/* Footer SG DIGITAL */}
          <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="text.secondary">
              Édité par <strong>SG DIGITAL</strong>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForgotPassword;
